'use client'

import { Children, type ReactNode } from 'react'

import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'

import type { OtherProvinceEventsAnalytics } from './types'

interface TrackedOtherProvinceGridProps {
  readonly className?: string
  readonly analytics: OtherProvinceEventsAnalytics
  /** Las cards ya renderizadas por el Server Component padre, en orden. */
  readonly children: ReactNode
}

/**
 * Envoltorio cliente del grid de "Eventos en otras ciudades": emite el
 * `view_item_list` (impresiones agrupadas al entrar cada card en viewport) y el
 * `select_item` de la card clicada.
 *
 * Recibe las cards como `children` en vez de renderizarlas: así `VerticalCard`
 * sigue siendo un Server Component y no entra en el bundle de cliente — solo
 * entra este envoltorio. Cada hijo se envuelve en `TrackedProductCard` por su
 * posición, que es la misma que la del ítem correspondiente en
 * `analytics.items` (contrato del tipo).
 */
export function TrackedOtherProvinceGrid({
  className,
  analytics,
  children,
}: TrackedOtherProvinceGridProps) {
  const pageAnalytics = useOptionalPageAnalytics()
  const { trackImpression, trackSelect } = useProductListTracking(analytics, {
    trackViewItemList: pageAnalytics?.trackViewItemList,
    trackSelectItem: pageAnalytics?.trackSelectItem,
  })

  return (
    <div className={className}>
      {Children.map(children, (child, index) => {
        const item = analytics.items[index]
        if (!item) return child

        return (
          <TrackedProductCard
            index={index}
            onImpression={() => trackImpression(item.item_id, withIndex(item, index))}
            onSelect={() => trackSelect(withIndex(item, index))}
          >
            {child}
          </TrackedProductCard>
        )
      })}
    </div>
  )
}
